/**
 * The minimal staff exceptions dashboard — CLAUDE.md's Reports section is
 * explicit this belongs on login, not behind navigation. Same plain
 * `api()` + `useEffect` shape as ReportsGlanceWidget.tsx, for the same
 * reason: one small fetch, not a paginated list.
 */

import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { Widget } from './Widget.js';
import { humanizeEnum } from './Form.js';

interface ExceptionRow {
  exceptionType: string;
  severity: string;
  entityId: string;
  entityLabel: string;
  detail: string;
  occurredAt: string | null;
}

export function ExceptionsWidget() {
  const [rows, setRows] = useState<ExceptionRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    // /api/reports/exceptions goes through sendReport (routes.ts), same as
    // every other report route — its JSON branch always wraps rows as
    // { data, total }, unlike /reports/summary's bare object.
    api<{ data: ExceptionRow[] }>('/reports/exceptions')
      .then((response) => {
        if (!cancelled) setRows(response.data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Widget icon="shield" tone="orange" title="Exceptions">
      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not load exceptions.'}
        </p>
      ) : rows === null ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">Nothing needs attention right now.</p>
      ) : (
        <ul className="stack" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {rows.map((row) => (
            <li key={`${row.exceptionType}-${row.entityId}`} className="row">
              <span className="pill pill--negative">{humanizeEnum(row.exceptionType)}</span>
              <div className="stack" style={{ gap: '0.1rem' }}>
                <strong>{row.entityLabel}</strong>
                <span className="muted" style={{ fontSize: '0.8rem' }}>
                  {row.detail}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}
